import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Phone, MoreVertical, Paperclip, Send, Mic, MapPin, ArrowLeft, Tags, Trash2, UploadCloud, Pin, Ban, Megaphone, Bot, Loader2, X } from 'lucide-react';
import { User, Message as MessageType } from '../../types';
import { getMessagesApi, sendMessageApi, clearChatHistoryApi, markMessagesReadApi } from '../../api/chats';
import { updateUserTagsApi } from '../../api/users';
import Message from './Message';
import { useSettings } from '../../App';
import QuickReplyPopup from './QuickReplyPopup';
import TagManagerModal from '../tags/TagManagerModal';
import FileUploadPreview from './FileUploadPreview';
import AskAIModal from '../ai/AskAIModal';
import LocationInputModal from './LocationInputModal';
import CtaModal from './CtaModal';
import { useOnClickOutside } from '../../hooks/useOnClickOutside'; // Import the hook

interface ChatWindowProps {
  user: User | null;
  onBack: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ user, onBack }) => {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [quotedMessage, setQuotedMessage] = useState<MessageType | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showQuickReplyPopup, setShowQuickReplyPopup] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(user);
  const [isPinned, setIsPinned] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isCtaModalOpen, setIsCtaModalOpen] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { chatRetention, isAiTakeoverGlobal } = useSettings();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);  // 滚动容器引用
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const headerMenuRef = useRef<HTMLDivElement>(null);
  const messageIdsRef = useRef<Set<string>>(new Set());
  const statusSyncRef = useRef<number>(0);
  const maxMessageIdRef = useRef<string | null>(null);  // 已知最大消息ID（since_id 增量同步游标）
  const fallbackSyncRef = useRef<NodeJS.Timeout | undefined>();  // 30分钟保底全量同步
  // 滚动控制：标记是否需要滚动到底部
  const pendingScrollRef = useRef<boolean>(false);
  // 上一个用户ID，用于检测用户切换
  const prevUserIdRef = useRef<string | null>(null);

  // Use useOnClickOutside hook
  useOnClickOutside(headerMenuRef, () => setIsHeaderMenuOpen(false));

  const retentionTextMap: Record<string, string> = { '7': '7 天', '30': '30 天', '60': '60 天', '90': '90 天', 'permanent': '永久' };

  // 滚动到底部的可靠函数
  const scrollToBottom = useCallback((instant: boolean = false) => {
    const container = messagesContainerRef.current;
    if (container) {
      if (instant) {
        container.scrollTop = container.scrollHeight;
      } else {
        // 使用 requestAnimationFrame 确保 DOM 更新完成
        requestAnimationFrame(() => {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        });
      }
    }
  }, []);

  useEffect(() => {
    const handleWsConnected = () => {
      if (!user) return;
      try { markMessagesReadApi(user.id).catch(() => { }); } catch { }
      (async () => {
        try {
          const userMessages = await getMessagesApi(user.id);
          const sortedMessages = userMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          setMessages(sortedMessages as any);
        } catch { }
      })();
    };
    window.addEventListener('ws_connected', handleWsConnected);
    return () => { window.removeEventListener('ws_connected', handleWsConnected); };
  }, [user]);
  useEffect(() => {
    if (user) {
      setCurrentUser(user);
      setPendingFiles([]);
      setNewMessage('');
      setQuotedMessage(null);
      setIsPinned(false);
      setIsBlocked(false);

      window.dispatchEvent(new CustomEvent('chat_opened', { detail: { userId: user.id } }));
      try { markMessagesReadApi(user.id).catch(() => { }); } catch { }

      const fetchMessages = async () => {
        setIsLoading(true);
        try {
          const userMessages = await getMessagesApi(user.id);
          const sortedMessages = userMessages.sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          const unique: MessageType[] = [];
          const idSet = new Set<string>();
          const waSet = new Set<string>();
          for (const m of sortedMessages) {
            const hasId = idSet.has(m.id);
            const hasWa = m.wa_id ? waSet.has(m.wa_id) : false;
            if (hasId || hasWa) continue;
            idSet.add(m.id);
            if (m.wa_id) waSet.add(m.wa_id);
            unique.push(m);
          }
          setMessages(prev => {
            const merged = unique.map(m => {
              const ex = prev.find(pm => (pm.id === m.id) || (!!pm.wa_id && !!m.wa_id && pm.wa_id === m.wa_id));
              let next = m as any;
              if (ex && (ex as any).cta && !(next as any).cta) next = { ...next, cta: (ex as any).cta };
              if (ex && (ex as any).ctaHeader && !(next as any).ctaHeader) next = { ...next, ctaHeader: (ex as any).ctaHeader };
              if (ex && (ex as any).ctaBody && !(next as any).ctaBody) next = { ...next, ctaBody: (ex as any).ctaBody };
              if (ex && (ex as any).ctaFooter && !(next as any).ctaFooter) next = { ...next, ctaFooter: (ex as any).ctaFooter };
              return next;
            });
            messageIdsRef.current = new Set(merged.map(m => m.id));
            // 标记需要滚动到底部（由 useLayoutEffect 处理）
            pendingScrollRef.current = true;
            return merged as any;
          });
        } catch (error) {
          console.error("[ChatWindow] Failed to fetch messages:", error);
          // 提供更好的错误反馈
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error("[ChatWindow] Error details:", {
            message: errorMessage,
            userId: user.id,
            timestamp: new Date().toISOString()
          });
          setMessages([]);
          // 可选：显示用户友好的错误消息
          window.dispatchEvent(new CustomEvent('api_error', {
            detail: { type: 'fetch_messages', error: errorMessage, userId: user.id }
          }));
        } finally {
          setIsLoading(false);
        }
      };
      fetchMessages();
    } else {
      setCurrentUser(null);
      setMessages([]);
    }
  }, [user]);

  useEffect(() => {
    const handleNewMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      let newMessage = customEvent.detail as MessageType;
      if (!currentUser) return;
      if (newMessage.user_id !== currentUser.id) return;
      setMessages(prev => {
        const existed = prev.find(m => (m.id === newMessage.id) || (!!m.wa_id && !!newMessage.wa_id && m.wa_id === newMessage.wa_id));
        const payload = (customEvent.detail && (customEvent.detail as any).interactive_payload) || undefined;
        const nowTs = new Date(newMessage.timestamp).getTime() || Date.now();
        let tempIdx = prev.findIndex(m => m.id.startsWith('temp-cta-') && m.sender === 'me' && m.type === 'text' && Math.abs(new Date(m.timestamp).getTime() - nowTs) < 60000);
        if (tempIdx < 0) {
          // 后备：按内容前缀近似匹配
          tempIdx = prev.findIndex(m => m.id.startsWith('temp-cta-') && m.sender === 'me' && m.type === 'text' && String(m.content || '').startsWith('[CTA]'));
        }
        if (existed || tempIdx >= 0 || payload) {
          const source = (existed || prev[tempIdx]) as any;
          const nmAny = newMessage as any;
          const header = payload?.header || undefined;
          const bodyText = payload?.body?.text || undefined;
          const footerText = payload?.footer?.text || undefined;
          const btn = payload?.action?.parameters || undefined;
          if (source?.cta && !nmAny.cta) nmAny.cta = source.cta;
          if (!nmAny.cta && btn?.display_text && btn?.url) nmAny.cta = { displayText: btn.display_text, url: btn.url };
          if (source?.ctaHeader && !nmAny.ctaHeader) nmAny.ctaHeader = source.ctaHeader;
          if (!nmAny.ctaHeader && header?.type === 'text' && header?.text) nmAny.ctaHeader = { type: 'text', content: header.text };
          if (!nmAny.ctaHeader && header?.type === 'image' && header?.image?.link) nmAny.ctaHeader = { type: 'image', content: header.image.link };
          if (source?.ctaBody && !nmAny.ctaBody) nmAny.ctaBody = source.ctaBody;
          if (!nmAny.ctaBody && bodyText) nmAny.ctaBody = bodyText;
          if (source?.ctaFooter && !nmAny.ctaFooter) nmAny.ctaFooter = source.ctaFooter;
          if (!nmAny.ctaFooter && footerText) nmAny.ctaFooter = footerText;
          newMessage = nmAny as MessageType;
        }
        const filtered = prev.filter((m, idx) => {
          const sameId = m.id === newMessage.id;
          const sameWa = !!m.wa_id && !!newMessage.wa_id && m.wa_id === newMessage.wa_id;
          if (tempIdx >= 0 && idx === tempIdx) return false; // 移除乐观消息
          return !(sameId || sameWa);
        });
        const updated = [...filtered, newMessage];
        messageIdsRef.current.add(newMessage.id);
        // 标记需要滚动到底部（由 useLayoutEffect 处理）
        pendingScrollRef.current = true;
        return updated.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      });
    };

    const handleStatusUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const update = customEvent.detail as { message_id: string; status: MessageType['status']; user_id: string; wa_id?: string };
      if (!currentUser || update.user_id !== currentUser.id) return;
      let matched = false;
      setMessages(prev => {
        const next = prev.map(m => {
          const isTarget = (m.id === update.message_id) || (m.wa_id && (m.wa_id === update.message_id || (update.wa_id && m.wa_id === update.wa_id)));
          if (isTarget) {
            matched = true;
            return { ...m, status: update.status };
          }
          return m;
        });
        return next;
      });
      if (!matched) {
        const now = Date.now();
        if (now - statusSyncRef.current > 2000) {
          statusSyncRef.current = now;
          getMessagesApi(update.user_id).then(userMessages => {
            const sorted = userMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            const unique: MessageType[] = [];
            const idSet = new Set<string>();
            const waSet = new Set<string>();
            for (const m of sorted) {
              const hasId = idSet.has(m.id);
              const hasWa = m.wa_id ? waSet.has(m.wa_id) : false;
              if (hasId || hasWa) continue;
              idSet.add(m.id);
              if (m.wa_id) waSet.add(m.wa_id);
              unique.push(m);
            }
            setMessages(prev => unique.map(m => {
              const ex = prev.find(pm => (pm.id === m.id) || (!!pm.wa_id && !!m.wa_id && pm.wa_id === m.wa_id));
              let next = m as any;
              if (ex && (ex as any).cta && !(next as any).cta) next = { ...next, cta: (ex as any).cta };
              if (ex && (ex as any).ctaHeader && !(next as any).ctaHeader) next = { ...next, ctaHeader: (ex as any).ctaHeader };
              if (ex && (ex as any).ctaBody && !(next as any).ctaBody) next = { ...next, ctaBody: (ex as any).ctaBody };
              if (ex && (ex as any).ctaFooter && !(next as any).ctaFooter) next = { ...next, ctaFooter: (ex as any).ctaFooter };
              return next as MessageType;
            }));
          }).catch(() => { });
        }
      }
    };

    window.addEventListener('new_message', handleNewMessage);
    window.addEventListener('message_status_update', handleStatusUpdate);
    return () => {
      window.removeEventListener('new_message', handleNewMessage);
      window.removeEventListener('message_status_update', handleStatusUpdate);
    };
  }, [currentUser]);

  // ===== 智能轮询策略 (WebSocket 优先，轮询仅作保底) =====
  // 追踪已知最大消息ID，用于 since_id 增量同步（避免时钟偏差）
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1]; // 按时间戳升序排列
      if (!maxMessageIdRef.current || lastMsg.id > maxMessageIdRef.current) {
        maxMessageIdRef.current = lastMsg.id;
      }
    }
  }, [messages]);

  // 当前用户 ID 追踪
  const currentUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentUserIdRef.current = currentUser?.id ?? null;
  }, [currentUser?.id]);

  // 主轮询逻辑：WebSocket 在线时零轮询，断线重连后增量同步，30分钟保底全量校对
  useEffect(() => {
    // 清除之前的定时器
    if (fallbackSyncRef.current) {
      clearInterval(fallbackSyncRef.current);
      fallbackSyncRef.current = undefined;
    }

    if (currentUser && !isBlocked) {
      // 保底机制：每 30 分钟做一次全量校对同步（替代原来的 60 秒全量拉取）
      fallbackSyncRef.current = setInterval(async () => {
        const userId = currentUserIdRef.current;
        if (!userId) return;
        try {
          const userMessages = await getMessagesApi(userId);
          const sortedMessages = userMessages.sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          setMessages(prev => {
            const currentIds = new Set(prev.map(m => m.id));
            const newMessages = sortedMessages.filter(m => !currentIds.has(m.id));
            if (newMessages.length > 0) {
              const merged = [...prev, ...newMessages].sort((a, b) =>
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              );
              return merged;
            }
            return prev;
          });
        } catch (error) {
          console.error('[ChatWindow] Fallback sync failed:', error);
        }
      }, 30 * 60 * 1000); // 30 分钟

      // WebSocket 重连后使用 since_id 增量同步（基于消息ID游标，避免时钟偏差）
      const handleWsReconnected = async () => {
        const userId = currentUserIdRef.current;
        if (!userId || !maxMessageIdRef.current) return;
        try {
          const newMessages = await getMessagesApi(userId, { since_id: maxMessageIdRef.current });
          if (newMessages.length > 0) {
            setMessages(prev => {
              const currentIds = new Set(prev.map(m => m.id));
              const trulyNew = newMessages.filter(m => !currentIds.has(m.id));
              if (trulyNew.length > 0) {
                const merged = [...prev, ...trulyNew].sort((a, b) =>
                  new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                );
                return merged;
              }
              return prev;
            });
          }
        } catch (error) {
          console.error('[ChatWindow] Reconnect sync failed:', error);
        }
      };
      window.addEventListener('ws_connected', handleWsReconnected);
      return () => {
        window.removeEventListener('ws_connected', handleWsReconnected);
      };
    }

    return () => {
      if (fallbackSyncRef.current) {
        clearInterval(fallbackSyncRef.current);
        fallbackSyncRef.current = undefined;
      }
    };
  }, [currentUser?.id, isBlocked]);

  // 使用 useLayoutEffect 确保在 DOM 更新后立即滚动
  useLayoutEffect(() => {
    if (pendingScrollRef.current) {
      scrollToBottom(false);
      pendingScrollRef.current = false;
    }
  }, [messages, scrollToBottom]);

  // 用户切换时强制滚动到底部
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    const isUserChanged = prevUserIdRef.current !== currentUserId;
    prevUserIdRef.current = currentUserId;

    if (user && !isLoading && messages.length > 0 && isUserChanged) {
      // 用户切换时立即滚动（无动画）
      setTimeout(() => {
        scrollToBottom(true);
      }, 50);
    }
  }, [user?.id, isLoading, messages.length, scrollToBottom]);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setPendingFiles(prev => {
      const uniqueNewFiles = newFiles.filter(nf => !prev.some(pf => pf.name === nf.name && pf.size === nf.size));
      return [...prev, ...uniqueNewFiles];
    });
  }, []);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      handleFiles(e.clipboardData?.files ?? null);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemovePendingFile = (fileName: string) => {
    setPendingFiles(prev => prev.filter(f => f.name !== fileName));
    setUploadProgress(prev => {
      const next = { ...prev };
      delete next[fileName];
      return next;
    });
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && pendingFiles.length === 0) || !currentUser || isBlocked) return;

    if (pendingFiles.length > 0) {
      const uploads = pendingFiles.map(async (file) => {
        try {
          const { uploadFileApi } = await import('../../api/upload');
          const result = await uploadFileApi(file, (p) => setUploadProgress(prev => ({ ...prev, [file.name]: p })));
          const tempId = `temp-${file.name}-${file.lastModified}-${Date.now()}`;
          const msgType = result.type as MessageType['type'];
          const optimistic: MessageType = {
            id: tempId,
            sender: 'me',
            type: msgType,
            content: result.previewUrl || result.url,
            timestamp: new Date().toISOString(),
            status: 'sending',
          };
          messageIdsRef.current.add(tempId);
          setMessages(prev => [...prev, optimistic]);
          const sent = await sendMessageApi(currentUser.id, { type: msgType, content: result.url });
          setMessages(prev => {
            const filtered = prev.filter(m => m.id !== tempId && !(m.id === sent.id || (m.wa_id && sent.wa_id && m.wa_id === sent.wa_id)));
            return [...filtered, { ...sent, status: 'sent' }];
          });
        } catch (err) {
          console.error('文件上传或发送失败:', err);
        }
      });
      await Promise.all(uploads);
      setPendingFiles([]);
      setUploadProgress({});
    }

    if (newMessage.trim()) {
      const tempId = `temp-${Date.now()}`;
      const optimisticMessage: MessageType = {
        id: tempId,
        sender: 'me',
        type: 'text',
        content: newMessage,
        timestamp: new Date().toISOString(),
        status: 'sending',
      };
      messageIdsRef.current.add(tempId);
      setMessages(prev => [...prev, optimisticMessage]);
      setNewMessage('');
      setShowQuickReplyPopup(false);
      try {
        const sentMessage = await sendMessageApi(currentUser.id, {
          type: 'text',
          content: optimisticMessage.content,
          quotedMessageId: quotedMessage?.id
        });
        setQuotedMessage(null);
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== tempId && !(m.id === sentMessage.id || (m.wa_id && sentMessage.wa_id && m.wa_id === sentMessage.wa_id)));
          return [...filtered, { ...sentMessage, status: 'sent' }];
        });
      } catch (error) {
        console.error('[ChatWindow] Text message send failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('[ChatWindow] Send error details:', {
          message: errorMessage,
          userId: currentUser.id,
          tempId,
          content: optimisticMessage.content
        });
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'failed' } : m));
        // 显示用户友好的错误提示
        window.dispatchEvent(new CustomEvent('send_error', {
          detail: { type: 'text', error: errorMessage, tempId }
        }));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      // If files are pending, Enter should send them immediately without opening file dialog
      // Check if we have pending files that haven't been sent yet
      // If so, handleSendMessage will handle them.
      // We just need to ensure we don't trigger the file input if it's not intended.
      handleSendMessage();
    } else if (e.key === '/' && newMessage === '') {
      setShowQuickReplyPopup(true);
    } else if (showQuickReplyPopup && (e.key === 'Escape' || newMessage === '')) {
      setShowQuickReplyPopup(false);
    }
  };

  const handleQuickReplySelect = (reply: string) => {
    setNewMessage(reply);
    setShowQuickReplyPopup(false);
    inputRef.current?.focus();
  };

  const handleUpdateTags = async (newTags: string[]) => {
    if (!currentUser) return;
    try {
      const ids = newTags.map(t => parseInt(t, 10)).filter(n => !Number.isNaN(n));
      const updated = await updateUserTagsApi(currentUser.id, ids as any);
      setCurrentUser({ ...currentUser, tags: updated.tags });
    } catch (error) {
      console.error("Failed to update tags:", error);
    }
  };

  const handleTogglePin = () => setIsPinned(!isPinned);
  const handleToggleBlock = () => setIsBlocked(!isBlocked);

  // 点击红色 X 重发失败消息
  const handleRetry = async (failedMsg: MessageType) => {
    if (!currentUser) return;
    // 改为 sending 状态（图标变旋转 loading，防止重复点击）
    setMessages(prev => prev.map(m =>
      m.id === failedMsg.id ? { ...m, status: 'sending' as const } : m
    ));
    try {
      const newMsg = await sendMessageApi(currentUser.id, {
        type: failedMsg.type,
        content: failedMsg.content,
      });
      // 替换为新消息（新 ID / wa_id / status）
      setMessages(prev => prev.map(m =>
        m.id === failedMsg.id ? { ...newMsg, status: newMsg.status || 'sent' as const } : m
      ));
    } catch {
      // 失败恢复为 failed
      setMessages(prev => prev.map(m =>
        m.id === failedMsg.id ? { ...m, status: 'failed' as const } : m
      ));
    }
  };

  const handleClearChat = async () => {
    if (!currentUser) return;
    if (window.confirm('确定要清除与此用户的所有聊天记录吗？此操作不可撤销。')) {
      try {
        await clearChatHistoryApi(currentUser.id);
        setMessages([]);
        messageIdsRef.current.clear();
        // Dispatch event to update UserList immediately
        window.dispatchEvent(new CustomEvent('chat_cleared', { detail: { userId: currentUser.id } }));
      } catch (error) {
        console.error("Failed to clear chat:", error);
      }
    }
  };

  const handleQuoteMessage = (message: MessageType) => {
    setQuotedMessage(message);
    inputRef.current?.focus();
  };

  const handleScrollToQuote = (quotedMsg: any) => {
    const key = quotedMsg?.wa_id || quotedMsg?.id;
    const el = key ? document.getElementById(`msg-${key}`) : null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('bg-accent/20');
      setTimeout(() => el.classList.remove('bg-accent/20'), 2000);
    } else {
      if (!currentUser) return;
      getMessagesApi(currentUser.id).then(userMessages => {
        const target = userMessages.find(m => (m.wa_id && quotedMsg?.wa_id && m.wa_id === quotedMsg.wa_id) || m.id === quotedMsg?.id);
        if (target) {
          setMessages(prev => {
            const merged = [...prev];
            const exists = merged.some(m => m.id === target.id || (m.wa_id && target.wa_id && m.wa_id === target.wa_id));
            if (!exists) merged.push(target);
            return merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          });
          setTimeout(() => {
            const el2 = document.getElementById(`msg-${target.wa_id ?? target.id}`);
            if (el2) {
              el2.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el2.classList.add('bg-accent/20');
              setTimeout(() => el2.classList.remove('bg-accent/20'), 2000);
            }
          }, 100);
        }
      }).catch(() => { });
    }
  };

  if (!user || !currentUser) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-background text-muted-foreground">
        <div className="bg-muted/30 p-8 rounded-full mb-4">
          <Phone className="w-12 h-12 opacity-50" />
        </div>
        <p className="text-lg">选择一个联系人开始聊天</p>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col h-full relative bg-background"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      ref={dropZoneRef}
    >
      {isDragging && (
        <div className="absolute inset-0 bg-primary/10 backdrop-blur-sm z-50 flex flex-col items-center justify-center border-2 border-dashed border-primary m-4 rounded-lg animate-pulse">
          <UploadCloud className="w-16 h-16 text-primary mb-4" />
          <p className="text-xl font-semibold text-primary">释放文件以发送</p>
        </div>
      )}

      {isTagModalOpen && (
        <TagManagerModal
          user={currentUser}
          onClose={() => setIsTagModalOpen(false)}
          onSave={handleUpdateTags}
        />
      )}

      {isAiModalOpen && (
        <AskAIModal
          isOpen={isAiModalOpen}
          onClose={() => setIsAiModalOpen(false)}
          onSend={(text) => setNewMessage(text)}
        />
      )}

      {isLocationModalOpen && (
        <LocationInputModal
          isOpen={isLocationModalOpen}
          onClose={() => setIsLocationModalOpen(false)}
          onSend={(location) => {
            if (!currentUser) return;
            const tempId = `temp-loc-${Date.now()}`;
            // 构建包含所有位置信息的内容
            let locationContent = `${location.lat},${location.lon}`;
            if (location.name || location.address) {
              const details = [];
              if (location.name) details.push(location.name);
              if (location.address) details.push(location.address);
              locationContent += ` (${details.join(' - ')})`;
            }

            const optimistic: MessageType = {
              id: tempId,
              sender: 'me',
              type: 'location',
              content: locationContent,
              timestamp: new Date().toISOString(),
              status: 'sending',
            };
            setMessages(prev => [...prev, optimistic]);
            (async () => {
              try {
                // 构建发送给后端的位置对象，符合WhatsApp API格式
                const locationObj = {
                  lat: parseFloat(location.lat),
                  lon: parseFloat(location.lon),
                  name: location.name,
                  address: location.address
                };
                const sent = await sendMessageApi(currentUser.id, {
                  type: 'location',
                  content: locationObj,
                  quotedMessageId: quotedMessage?.id
                });
                setMessages(prev => prev.map(m => m.id === tempId ? { ...sent, type: 'location', content: optimistic.content, status: 'sent' } : m));
              } catch (e) {
                setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'failed' } : m));
              } finally {
                setIsLocationModalOpen(false);
              }
            })();
          }}
        />
      )}

      {isCtaModalOpen && (
        <CtaModal
          isOpen={isCtaModalOpen}
          onClose={() => setIsCtaModalOpen(false)}
          onSend={async (cta) => {
            if (!currentUser) return;
            try {
              const displayText = cta.displayText || (cta?.interactive?.action?.parameters?.display_text || 'CTA');
              const optimistic: MessageType = {
                id: `temp-cta-${Date.now()}`,
                sender: 'me',
                type: 'text',
                content: `[CTA] ${displayText}`,
                timestamp: new Date().toISOString(),
                status: 'sending' as const,
                cta: { displayText, url: cta?.interactive?.action?.parameters?.url || '' },
                ctaHeader: (() => {
                  const header = cta?.interactive?.header;
                  if (!header || !header.type) return undefined;
                  if (header.type === 'text' && header.text) return { type: 'text', content: header.text };
                  if (header.type === 'image' && header.image?.link) return { type: 'image', content: header.image.link };
                  return undefined;
                })()
                ,
                ctaBody: cta?.interactive?.body?.text || displayText,
                ctaFooter: cta?.interactive?.footer?.text || ''
              };
              setMessages(prev => [...prev, optimistic]);
              const sent = await sendMessageApi(currentUser.id, {
                type: 'text',
                content: `[CTA] ${displayText}`,
                interactivePayload: cta?.interactive || {
                  type: 'cta_url',
                  header: cta?.interactive?.header,
                  body: { text: cta?.interactive?.body?.text || displayText },
                  action: { name: 'cta_url', parameters: { display_text: displayText, url: cta?.interactive?.action?.parameters?.url || '' } },
                },
                quotedMessageId: quotedMessage?.id,
              });
              setMessages(prev => prev.map(m => (m.id === optimistic.id ? { ...sent, status: 'sent', type: 'text', content: `[CTA] ${displayText}`, cta: optimistic.cta, ctaHeader: optimistic.ctaHeader, ctaBody: optimistic.ctaBody, ctaFooter: optimistic.ctaFooter } : m)));
            } catch (e) {
              console.error('发送 CTA 失败', e);
            } finally {
              setIsCtaModalOpen(false);
            }
          }}
        />
      )}

      <header className="flex items-center justify-between px-6 py-3 border-b border-border bg-card shadow-sm z-20">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="md:hidden text-muted-foreground hover:text-foreground p-1">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="relative group cursor-pointer">
            <img
              src={currentUser.avatar}
              alt={currentUser.name}
              className="w-10 h-10 rounded-full object-cover border border-border group-hover:opacity-80 transition-opacity"
              onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.name)}&background=random` }}
            />
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-background rounded-full"></div>
          </div>
          <div>
            <h2 className="font-semibold flex items-center gap-2">
              {currentUser.name}
              {isPinned && <Pin className="w-3 h-3 text-primary fill-primary" />}
              {isBlocked && <Ban className="w-3 h-3 text-destructive" />}
            </h2>
            <p className="text-xs text-muted-foreground">在线 • +{currentUser.phone}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 relative">
          <button
            onClick={() => setIsTagModalOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-input border border-border rounded-md text-sm hover:bg-muted transition-colors"
            title="添加标签"
          >
            <Tags className="w-4 h-4" />
            <span>标签</span>
          </button>
          {/* Add Manage Tags button separately if needed, but TagManagerModal usually handles both add/remove */}

          <div className="relative" ref={headerMenuRef}>
            <button
              onClick={() => setIsHeaderMenuOpen(!isHeaderMenuOpen)}
              className="p-2 hover:bg-muted rounded-full text-muted-foreground hover:text-foreground transition-colors"
            >
              <MoreVertical className="w-5 h-5" />
            </button>

            {isHeaderMenuOpen && (
              <div className="absolute right-0 top-12 w-56 bg-popover border border-border rounded-md shadow-lg py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-xs font-medium text-muted-foreground">聊天设置</p>
                </div>
                <button onClick={handleTogglePin} className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-center gap-2">
                  <Pin className="w-4 h-4" /> {isPinned ? '取消置顶' : '置顶聊天'}
                </button>
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-center gap-2 text-muted-foreground cursor-not-allowed">
                  <Bot className="w-4 h-4" /> AI 接管 (全局: {isAiTakeoverGlobal ? '开' : '关'})
                </button>
                <div className="border-t border-border my-1"></div>
                <div className="px-3 py-1.5">
                  <p className="text-xs text-muted-foreground mb-1">保留期限</p>
                  <div className="text-sm font-medium px-1">{retentionTextMap[chatRetention] || '永久'}</div>
                </div>
                <div className="border-t border-border my-1"></div>
                <button onClick={handleToggleBlock} className="w-full text-left px-4 py-2.5 text-sm hover:bg-destructive/10 text-destructive flex items-center gap-2">
                  <Ban className="w-4 h-4" /> {isBlocked ? '取消封锁' : '封锁用户'}
                </button>
                <button onClick={handleClearChat} className="w-full text-left px-4 py-2.5 text-sm hover:bg-destructive/10 text-destructive flex items-center gap-2">
                  <Trash2 className="w-4 h-4" /> 清空记录
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {isBlocked && (
              <div className="sticky top-2 bg-destructive/30 text-destructive-foreground p-2 rounded-lg text-center text-sm z-10 shadow-lg backdrop-blur-sm border border-destructive">
                此用户已被封锁。您无法发送消息。
              </div>
            )}
            {isAiTakeoverGlobal && !isBlocked && (
              <div className="sticky top-2 bg-accent/20 text-accent-foreground p-2 rounded-lg text-center text-xs z-10 shadow-lg backdrop-blur-sm">
                AI 接管已开启
              </div>
            )}
            {/* 消息列表区域 */}
            {messages.length === 0 ? (
              <div className="text-center text-muted-foreground p-8 h-full flex items-center justify-center">没有聊天记录。</div>
            ) : (
              <div className="space-y-4">
                {messages.map(msg => (
                  <div key={`msg-${msg.wa_id ?? msg.id}`} id={`msg-${msg.wa_id ?? msg.id}`}>
                    <Message
                      message={msg}
                      onReply={handleQuoteMessage}
                      onQuoteClick={handleScrollToQuote}
                      onRetry={handleRetry}
                    />
                  </div>
                ))}
              </div>
            )}
            {/* 滚动到底部的锤点 */}
            <div ref={messagesEndRef} style={{ height: 1 }} />
          </>
        )}
      </main>

      <footer className="bg-card border-t border-border relative z-10">
        <FileUploadPreview files={pendingFiles} onRemoveFile={handleRemovePendingFile} progressMap={uploadProgress} />
        {quotedMessage && (
          <div className="px-4 py-2 bg-muted/50 border-b border-border flex items-center justify-between animate-in slide-in-from-bottom-2">
            <div className="flex flex-col text-sm border-l-4 border-accent pl-2 overflow-hidden">
              <span className="text-accent font-bold">{quotedMessage.sender === 'me' ? '我' : '用户'}</span>
              <span className="text-muted-foreground truncate">{quotedMessage.content}</span>
            </div>
            <button onClick={() => setQuotedMessage(null)} className="p-1 hover:bg-muted rounded-full">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {showQuickReplyPopup && (
          <QuickReplyPopup
            onSelect={handleQuickReplySelect}
            search={newMessage.substring(1)}
          />
        )}
        <div className="flex items-center bg-input rounded-lg m-3 px-3 py-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            multiple
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            disabled={isBlocked}
          />
          <button onClick={() => fileInputRef.current?.click()} className="text-muted-foreground hover:text-primary p-2 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isBlocked}>
            <Paperclip />
          </button>
          <button onClick={() => setIsAiModalOpen(true)} className="text-muted-foreground hover:text-primary p-2 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isBlocked}>
            <Bot className="w-5 h-5" />
          </button>
          <button onClick={() => setIsLocationModalOpen(true)} className="text-muted-foreground hover:text-primary p-2 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isBlocked}>
            <MapPin />
          </button>
          <button onClick={() => setIsCtaModalOpen(true)} className="text-muted-foreground hover:text-accent p-2 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isBlocked}>
            <Megaphone className="w-5 h-5" />
          </button>
          <input
            ref={inputRef}
            type="text"
            placeholder={isBlocked ? "已封锁" : "输入消息"}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent border-none focus:ring-0 px-3 text-foreground placeholder:text-muted-foreground"
            disabled={isBlocked}
          />
          <div className="flex items-center gap-2">
            <button className="text-muted-foreground hover:text-primary p-2 disabled:opacity-50" disabled={isBlocked}>
              <Mic className="w-5 h-5" />
            </button>
            <button
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && pendingFiles.length === 0) || isBlocked}
              className="bg-whatsapp-green hover:bg-whatsapp-green/90 text-white p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ChatWindow;
